import React, { useState, useEffect } from 'react';
import { Form, Button, ProgressBar, Spinner, Alert } from 'react-bootstrap';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../firebase';

/**
 * Reusable bike form component for creating or editing motorcycle details
 * 
 * @param {Object} props - Component props
 * @param {Object} props.bike - The bike data (either new or being edited)
 * @param {Function} props.setBike - Function to update bike data
 * @param {Function} props.onSubmit - Function to handle form submission
 * @param {Function} props.onCancel - Function to handle cancellation
 * @param {string} props.submitButtonText - Text to display on the submit button
 * @param {boolean} props.isEdit - Whether this is an edit form (true) or create form (false)
 * @returns {JSX.Element} Rendered component
 */
const BikeForm = ({ bike, setBike, onSubmit, onCancel, submitButtonText = 'Save', isEdit = false }) => {
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previousPhoto, setPreviousPhoto] = useState(null);
  const [pendingPhotoUrl, setPendingPhotoUrl] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [imageStatus, setImageStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [isSubmitting, setIsSubmitting] = useState(false); // Track form submission state

  // Effect to check if the resized image is available
  useEffect(() => {
    if (!pendingPhotoUrl) return;
    
    const MAX_RETRIES = 10;
    const RETRY_DELAY = 2000; // 2 seconds between retries
    
    if (retryCount >= MAX_RETRIES) {
      console.error("Max retries reached while waiting for resized image");
      setImageStatus('error');
      // Use the original upload URL as fallback
      setBike({...bike, photo: pendingPhotoUrl});
      setPendingPhotoUrl(null);
      setRetryCount(0);
      setIsUploading(false);
      return;
    }

    setImageStatus('loading');
    
    // Check if the image exists
    const checkImage = () => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = pendingPhotoUrl;
      });
    };
    
    const timer = setTimeout(async () => {
      const imageExists = await checkImage();
      
      if (imageExists) {
        // console.log("Resized image loaded successfully");
        setBike({...bike, photo: pendingPhotoUrl});
        setImageStatus('success');
        setPendingPhotoUrl(null);
        setRetryCount(0);
        setIsUploading(false);
      } else {
        console.log(`Resized image not ready yet, retrying... (${retryCount + 1}/${MAX_RETRIES})`);
        setRetryCount(prevCount => prevCount + 1);
      }
    }, RETRY_DELAY);
    
    return () => clearTimeout(timer);
  }, [pendingPhotoUrl, retryCount, bike, setBike]);

  // Effect to animate the progress bar during file upload
  useEffect(() => {
    let progressInterval;
    
    if (isUploading && uploadProgress !== null && uploadProgress < 90) {
      progressInterval = setInterval(() => {
        setUploadProgress(prevProgress => {
          // Simulate progress that moves faster at the beginning and slows down as it approaches 90%
          const increment = Math.max(1, 10 - Math.floor(prevProgress / 10));
          const nextProgress = prevProgress + increment;
          return nextProgress < 90 ? nextProgress : 90;
        });
      }, 300);
    }
    
    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [isUploading, uploadProgress]);

  /**
   * Transform Firebase Storage URL to use the thumb version
   * 
   * @param {string} url - Original Firebase Storage URL (may be medium or original)
   * @returns {string} URL with _resized_thumb added before the file extension
   */
  const getThumbImageUrl = (url) => {
    if (!url) return url;
    
    // Check if URL is a Firebase Storage URL
    if (!url.includes('firebasestorage.googleapis.com')) return url;
    
    // Extract the filename from the URL
    const filenameMatch = url.match(/o\/([^?]+)/);
    if (!filenameMatch || filenameMatch.length < 2) return url;
    
    const encodedFilename = filenameMatch[1]; // This is URL encoded
    const decodedFilename = decodeURIComponent(encodedFilename);
    
    // Remove any existing resize suffix (in case it's already a medium version)
    const baseFilename = decodedFilename.replace(/_resized_(medium|thumb)/g, '');
    
    // Extract file extension and directory
    const lastDotIndex = baseFilename.lastIndexOf('.');
    if (lastDotIndex === -1) return url; // No extension found
    
    const lastSlashIndex = baseFilename.lastIndexOf('/');
    const directory = lastSlashIndex !== -1 ? baseFilename.substring(0, lastSlashIndex + 1) : '';
    const filename = lastSlashIndex !== -1 ? baseFilename.substring(lastSlashIndex + 1) : baseFilename;
    
    const filenameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const extension = filename.substring(filename.lastIndexOf('.'));
    
    // Create new filename with _resized_thumb suffix
    const newFilename = `${directory}${filenameWithoutExt}_resized_thumb${extension}`;
    const encodedNewFilename = encodeURIComponent(newFilename);
    
    // Replace the filename in the URL
    return url.replace(encodedFilename, encodedNewFilename);
  };

  /**
   * Transform Firebase Storage URL to use the resized medium version
   * 
   * @param {string} url - Original Firebase Storage URL
   * @returns {string} URL with _resized_medium added before the file extension
   */
  const getResizedImageUrl = (url) => {
    if (!url) return url;
    
    // Check if URL is a Firebase Storage URL
    if (!url.includes('firebasestorage.googleapis.com')) return url;
    
    // Extract the filename from the URL
    const filenameMatch = url.match(/o\/([^?]+)/);
    if (!filenameMatch || filenameMatch.length < 2) return url;
    
    const encodedFilename = filenameMatch[1]; // This is URL encoded
    const decodedFilename = decodeURIComponent(encodedFilename);
    
    // Extract file extension and directory
    const lastDotIndex = decodedFilename.lastIndexOf('.');
    if (lastDotIndex === -1) return url; // No extension found
    
    const lastSlashIndex = decodedFilename.lastIndexOf('/');
    const directory = lastSlashIndex !== -1 ? decodedFilename.substring(0, lastSlashIndex + 1) : '';
    const filename = lastSlashIndex !== -1 ? decodedFilename.substring(lastSlashIndex + 1) : decodedFilename;
    
    const filenmWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const extension = filename.substring(filename.lastIndexOf('.'));
    
    // Create new filename with _resized_medium suffix
    const newFilename = `${directory}${filenmWithoutExt}_resized_medium${extension}`;
    const encodedNewFilename = encodeURIComponent(newFilename);
    
    // Replace the filename in the URL
    return url.replace(encodedFilename, encodedNewFilename);
  };

  /**
   * Extract the base filename from a Firebase Storage URL
   * 
   * @param {string} url - Firebase Storage URL
   * @returns {string|null} Base filename without resize suffix or null if can't parse
   */
  const getBaseFilenameFromUrl = (url) => {
    if (!url) return null;
    
    // Extract the filename from the URL
    const filenameMatch = url.match(/o\/([^?]+)/);
    if (!filenameMatch || filenameMatch.length < 2) return null;
    
    const encodedFilename = filenameMatch[1];
    const decodedFilename = decodeURIComponent(encodedFilename);
    
    // Remove any resize suffix from the filename
    return decodedFilename.replace(/_resized_(medium|thumb)/g, '');
  };

  /**
   * Delete old image files from storage
   * 
   * @param {string} url - URL of the image to delete
   * @returns {Promise<void>}
   */
  const deleteOldImages = async (url) => {
    if (!url) return;

    try {
      const baseFilename = getBaseFilenameFromUrl(url);
      if (!baseFilename) {
        console.log('Error: Could not extract base filename from URL:', url);
        return;
      }

      console.log('Attempting to delete images with base filename:', baseFilename);

      const fileDir = baseFilename.includes('/') ? baseFilename.substring(0, baseFilename.lastIndexOf('/')) : '';
      const fileName = baseFilename.includes('/') ? baseFilename.substring(baseFilename.lastIndexOf('/') + 1) : baseFilename;
      const fileExt = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')) : '';
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));

      // Create references to both resized versions
      const mediumRef = ref(storage, `${fileDir}/${nameWithoutExt}_resized_medium${fileExt}`);
      const thumbRef = ref(storage, `${fileDir}/${nameWithoutExt}_resized_thumb${fileExt}`);

      // Delete both files
      await Promise.all([
        deleteObject(mediumRef).catch(err => console.log('Medium file may not exist:', err)),
        deleteObject(thumbRef).catch(err => console.log('Thumb file may not exist:', err))
      ]);

      // console.log('Successfully deleted old image files');
    } catch (error) {
      console.error('Error deleting old images:', error);
      // Don't throw the error - we don't want to interrupt the upload process
    }
  };

  /**
   * Handle file upload
   * 
   * @param {Event} e - Change event
   */
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type - strictly JPG, PNG, WebP only
    const allowedTypes = [
      'image/png',
      'image/jpeg',
      'image/webp'
    ];
    const allowedExtensions = /\.(jpg|jpeg|png|webp)$/i;

    // Check both MIME type and file extension
    if (!allowedTypes.includes(file.type) || !allowedExtensions.test(file.name)) {
      alert('Please select a PNG, JPG, or WebP image file.');
      e.target.value = ''; // Clear the file input
      return;
    }

    // Check file size (7MB limit)
    const MAX_FILE_SIZE = 7 * 1024 * 1024; // 7MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      alert('File is too large. Please choose an image under 7MB.');
      e.target.value = ''; // Clear the file input
      return;
    }

    // Check image dimensions before upload
    try {
      const dimensions = await getImageDimensions(file);
      const MIN_WIDTH = 1024;
      const MIN_HEIGHT = 768;
      
      if (dimensions.width < MIN_WIDTH || dimensions.height < MIN_HEIGHT) {
        alert(`Image is too small (${dimensions.width}x${dimensions.height}). Minimum required: ${MIN_WIDTH}x${MIN_HEIGHT} pixels.`);
        e.target.value = ''; // Clear the file input
        return;
      }
    } catch (error) {
      console.error("Error checking image dimensions:", error);
      // If we can't check dimensions, let the server validate it
    }

    try {
      // Store the previous photo URL before starting upload
      const previousPhotoUrl = bike.photo;
      setPreviousPhoto(previousPhotoUrl);
      
      // Clear current photo during upload to avoid showing old image
      setBike({...bike, photo: null});
      setIsUploading(true);
      setUploadProgress(0);
      
      // Delete old images if they exist
      if (previousPhotoUrl) {
        await deleteOldImages(previousPhotoUrl);
      }
      
      // Start the progress animation
      const storageRef = ref(storage, `bike-photos/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      
      // Set to 100% when upload is complete
      setUploadProgress(100);
      
      // Slight delay to show 100% before moving to the next phase
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const downloadURL = await getDownloadURL(storageRef);
      
      // Transform URL to use the resized version
      const resizedURL = getResizedImageUrl(downloadURL);
      // console.log("Original URL:", downloadURL);
      // console.log("Resized URL:", resizedURL);
      
      // Instead of setting it immediately, set it as pending and let the effect handle it
      setPendingPhotoUrl(resizedURL);
      setUploadProgress(null);
      // Note: isUploading remains true until the effect confirms the image is loaded
    } catch (error) {
      console.error("Error uploading file:", error);
      // Restore previous photo if upload fails
      setBike({...bike, photo: previousPhoto});
      setPreviousPhoto(null);
      setUploadProgress(null);
      setIsUploading(false);
      e.target.value = ''; // Clear the file input
    }
  };
  
  /**
   * Get image dimensions
   * 
   * @param {File} file - Image file
   * @returns {Promise<{width: number, height: number}>} Image dimensions
   */
  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        });
        URL.revokeObjectURL(img.src); // Clean up
      };
      img.onerror = () => {
        reject(new Error("Failed to load image"));
        URL.revokeObjectURL(img.src); // Clean up
      };
      img.src = URL.createObjectURL(file);
    });
  };

  /**
   * Handle form submission
   * 
   * @param {Event} e - Form submit event
   */
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!isUploading && !isSubmitting) {
      setIsSubmitting(true);
      onSubmit(e)
        .finally(() => {
          // Reset submission state if the form remains mounted
          setIsSubmitting(false);
        });
    }
  };

  return (
    <Form className="bike-form" onSubmit={handleFormSubmit}>
      <Form.Group className="mb-3">
        <Form.Label htmlFor="make">Make *</Form.Label>
        <Form.Control
          id="make"
          type="text"
          placeholder="e.g., Honda, Yamaha"
          value={bike.make}
          onChange={(e) => setBike({...bike, make: e.target.value})}
          required
        />
      </Form.Group>
      
      <Form.Group className="mb-3">
        <Form.Label htmlFor="model">Model *</Form.Label>
        <Form.Control
          id="model"
          type="text"
          placeholder="e.g., CB500X, Tenere 700"
          value={bike.model}
          onChange={(e) => setBike({...bike, model: e.target.value})}
          required
        />
      </Form.Group>
  
      <Form.Group className="mb-3">
        <Form.Label htmlFor="year">Year *</Form.Label>
        <Form.Control
          id="year"
          type="number"
          min="1900"
          max={new Date().getFullYear() + 1}
          value={bike.year}
          onChange={(e) => setBike({...bike, year: parseInt(e.target.value)})}
          required
        />
      </Form.Group>
  
      <Form.Group className="mb-3">
        <Form.Label htmlFor="nickname">Nickname</Form.Label>
        <Form.Control
          id="nickname"
          type="text"
          placeholder="Optional nickname for your bike"
          value={bike.nickname || ''}
          onChange={(e) => setBike({...bike, nickname: e.target.value})}
        />
      </Form.Group>
  
      <Form.Group className="mb-3">
        <Form.Label htmlFor="purchaseDate">Purchase Date</Form.Label>
        <Form.Control
          id="purchaseDate"
          type="date"
          value={bike.purchaseDate || ''}
          onChange={(e) => setBike({...bike, purchaseDate: e.target.value})}
        />
      </Form.Group>
  
      <Form.Group className="mb-3">
        <Form.Label htmlFor="initialMileage">Initial Mileage</Form.Label>
        <Form.Control
          id="initialMileage"
          type="number"
          min="0"
          placeholder="Mileage when acquired"
          value={bike.initialMileage || ''}
          onChange={(e) => setBike({...bike, initialMileage: e.target.value ? parseInt(e.target.value) : ''})}
        />
        {!isEdit && <Form.Text className="text-muted">Required for calculating cost per mile in TCO analysis</Form.Text>}
      </Form.Group>
  
      <Form.Group className="mb-3">
        <Form.Label htmlFor="purchasePrice">Purchase Price</Form.Label>
        <Form.Control
          id="purchasePrice"
          type="number"
          min="0"
          step="0.01"
          placeholder="Optional purchase price"
          value={bike.purchasePrice || ''}
          onChange={(e) => setBike({...bike, purchasePrice: e.target.value})}
        />
        {!isEdit && <Form.Text className="text-muted">Optional - useful for historical records, not used in TCO calculations</Form.Text>}
      </Form.Group>
  
      <Form.Group className="mb-3">
        <Form.Label htmlFor="photo">Photo (PNG, JPG, or WebP - min 1024x768)</Form.Label>
        <Form.Control
          id="photo-file"
          type="file"
          accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
          onChange={handleFileUpload}
          size="sm"
        />
        
        {isUploading && (
          <div className="mt-2">
            {uploadProgress !== null && (
              <div>
                <small className="text-muted mb-1 d-block">
                  Uploading... {uploadProgress}%
                </small>
                <ProgressBar 
                  now={uploadProgress} 
                  variant="primary" 
                  animated 
                  style={{ height: '8px' }}
                />
              </div>
            )}
            
            {uploadProgress === null && imageStatus === 'loading' && (
              <div className="d-flex align-items-center gap-2 mt-1">
                <small className="text-muted">
                  Processing image... (attempt {retryCount + 1})
                </small>
                <Spinner animation="border" size="sm" />
              </div>
            )}
            
            {uploadProgress === null && imageStatus !== 'loading' && (
              <div className="d-flex align-items-center gap-2 mt-1">
                <small className="text-muted">
                  Processing image...
                </small>
                <Spinner animation="border" size="sm" />
              </div>
            )}
          </div>
        )}
        
        {imageStatus === 'error' && (
          <Alert variant="warning" className="mt-2 py-2 px-3">
            The resized image could not be loaded. Using original image instead.
          </Alert>
        )}
        
        {!isUploading && bike.photo && (
          <div className="mt-2">
            <img 
              src={getThumbImageUrl(bike.photo)} 
              alt="Current motorcycle" 
              className="img-thumbnail"
              style={{ maxWidth: '150px' }} 
            />
          </div>
        )}
      </Form.Group>
  
      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button
          variant="secondary"
          type="button"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="success"
          type="submit"
          disabled={!bike.make || !bike.model || !bike.year || isUploading || isSubmitting}
          title={isUploading ? "Please wait for image upload to complete" : 
                isSubmitting ? "Saving..." : ""}
        >
          {isUploading ? "Uploading..." : isSubmitting ? "Saving..." : submitButtonText}
        </Button>
      </div>
    </Form>
  );
};

export default BikeForm;